import { reverse, round } from 'lodash';
import { useEffect, useState } from 'react';

interface IProps {
  tableId: string;
  innerElementColIds?: string[];
  elementsToScroll?: number;
  speed?: number;
  stepDistance?: number;
}

const DEFAULT_ELEMENTS_TO_SCROLL = 3;
const DEFAULT_SPEED = 5;
const DEFAULT_STEP_DISTANCE = 10;

export const useAgGridScrollButtons = ({
  tableId,
  innerElementColIds,
  elementsToScroll = DEFAULT_ELEMENTS_TO_SCROLL,
  speed = DEFAULT_SPEED,
  stepDistance = DEFAULT_STEP_DISTANCE,
}: IProps) => {
  const [isScrollLeftDisabled, setIsScrollLeftDisabled] = useState(true);
  const [isScrollRightDisabled, setIsScrollRightDisabled] = useState(false);

  const element = document.querySelector(
    // eslint-disable-next-line max-len
    `#${tableId} .ag-body-horizontal-scroll-viewport:not(#${tableId} .ag-body.ag-layout-normal .ag-body-horizontal-scroll-viewport)`
  );

  const scroll = (isLeftScroll = false) => {
    if (!element) {
      return;
    }

    const distance = getFirstXVisibleElementsWidth(elementsToScroll, isLeftScroll);

    let scrollAmount = 0;
    const slideTimer = setInterval(() => {
      element.scrollLeft += isLeftScroll ? -stepDistance : stepDistance;

      scrollAmount += Math.abs(stepDistance);

      if (scrollAmount >= distance) {
        clearInterval(slideTimer);
      }
    }, speed);
  };

  const getFirstXVisibleElementsWidth = (number: number, isLeftScroll = false) => {
    const elementRect = element.getBoundingClientRect();

    const innerElements = getInnerElements(isLeftScroll);

    const firstVisibleElementIndex = getFirstVisibleElementIndex(innerElements, isLeftScroll);
    if (firstVisibleElementIndex < 0) {
      return 0;
    }

    let width = 0;
    for (let i = firstVisibleElementIndex; i < firstVisibleElementIndex + number; i++) {
      const innerElement = innerElements[i];
      const innerElementRect = innerElement?.getBoundingClientRect();

      // If is not left scroll and inner element is not fully visible (left side) get visible width
      if (!isLeftScroll && round(innerElementRect.left) < round(elementRect.left)) {
        width += innerElement?.clientWidth - (elementRect.left - innerElementRect.left);

        continue;
      }

      // If is left scroll and inner element is not fully visible (right side) get visible width
      if (isLeftScroll && round(innerElementRect.right) > round(elementRect.right)) {
        width += elementRect.right - innerElementRect.left;

        continue;
      }

      width += innerElement?.clientWidth;
    }

    return width;
  };

  const getInnerElements = (reverseArray = false) => {
    const innerElements = [];

    innerElementColIds?.forEach((colId) => {
      const innerElement = document.querySelector(`div.ag-header-cell[col-id="${colId}"]`);
      if (innerElement) {
        innerElements.push(innerElement);
      }
    });

    return reverseArray ? reverse(innerElements) : innerElements;
  };

  const getFirstVisibleElementIndex = (innerElements: Element[], fromRight = false) => {
    for (let i = 0; i < innerElements.length; i++) {
      if (isElementInViewport(innerElements[i], fromRight)) {
        return i;
      }
    }

    return -1;
  };

  const isElementInViewport = (innerElement: Element, fromRight = false) => {
    const rect = innerElement.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    if (fromRight) {
      return rect.right >= elementRect.right && rect.left < elementRect.right;
    }

    return rect.left <= elementRect.left && rect.right > elementRect.left;
  };

  useEffect(() => {
    if (!element) {
      return;
    }

    element.addEventListener('scroll', () => {
      setIsScrollLeftDisabled(element.scrollLeft === 0);
      setIsScrollRightDisabled(
        Math.round(element.scrollLeft) === element.scrollWidth - element.clientWidth
      );
    });
  }, [element]);

  return {
    isScrollLeftDisabled,
    isScrollRightDisabled,
    scroll,
  };
};
